<script>
import { findFirstFocusableElement } from '@src/utils/focus'
import { keyEvents } from '@utils/keyevents'
/**
 * This component provides a way to manage multiple popovers in the same parent component.
 * It provides a way to close the popovers by pressing the escape key.
 * It also provides a way to add custom functions to be called when the escape key is pressed.
 */
export default {
  name: 'PopupManagerProvider',
  provide() {
    const popupContext = {}

    Object.defineProperty(popupContext, 'popupManagerData', {
      enumerable: true,
      get: () => this.popupManagerData,
    })

    Object.defineProperty(popupContext, 'additionalMethods', {
      enumerable: true,
      get: () => this.additionalMethods,
    })
    return { popupContext }
  },
  data() {
    return {
      popupManagerData: [],
      additionalMethods: [],
    }
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.popupManagerData = []
        this.additionalMethods = []
      },
    },
    popupManagerData: {
      handler(newValue) {
        if (
          Array.isArray(newValue) &&
          newValue.length > 0 &&
          newValue[newValue.length - 1] &&
          typeof newValue[newValue.length - 1] === 'object'
        ) {
          const lastItem = newValue[newValue.length - 1]
          const key = Object.keys(lastItem)[0]

          if (key && key.startsWith('popover-')) {
            const focusableData = findFirstFocusableElement(
              lastItem[key]?.refData
            )

            if (focusableData) {
              if (Array.isArray(focusableData)) {
                focusableData[0].focus()
              }
              focusableData.focus()
            }
          }
        }
      },
    },
  },
  mounted() {
    /**
     * This event listener is used to listen for the escape key being pressed.
     * When the escape key is pressed, it calls the handleKeyDown method.
     */
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    /**
     * This method is used to remove the event listener when the component is destroyed.
     */
    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    /**
     * This method is used to call the additional methods that need to be called when the escape key is pressed.
     * It takes a single argument, which is the name of the function to be called.
     */
    callAdditionalFunctions(functionKey) {
      const fn = this.findFunctionKey(functionKey)
      if (fn) {
        const functionToCall = Object.values(fn)[0]
        if (typeof functionToCall === 'function') {
          functionToCall() // Call the function if it exists and is a function
        }
      }
    },
    /**
     * This method is used to find the function associated with the provided key.
     * It returns the object containing the function.
     */
    findFunctionKey(functionKey) {
      const item = this.additionalMethods.find((item) =>
        item.hasOwnProperty(functionKey)
      )
      return item || null
    },
    /**
     * This method is used to handle the keydown event.
     * It checks if the escape key is pressed and if it is, it calls the handleClosePicker method.
     */
    handleKeyDown(event) {
      // Check if the key pressed is "Escape"
      if (event.key === keyEvents.ESCAPE) {
        event.preventDefault()
        event.stopPropagation()
        // Loop through popupmanagerdata to find the key dynamically
        const lastPickerIndex = this.popupManagerData.length - 1

        if (lastPickerIndex !== -1) {
          const handleClosePicker = Object.values(
            this.popupManagerData[lastPickerIndex]
          )[0]

          if (handleClosePicker) {
            // Call the function to hide the dropdown
            if (
              Object.keys(this.popupManagerData[lastPickerIndex])[0].startsWith(
                'popover-'
              ) &&
              !handleClosePicker.isNotPicker
            ) {
              this.callAdditionalFunctions('destroySearchPopover')
            }
            handleClosePicker()
          }
        }
      }
    },
  },
  render() {
    return this.$scopedSlots.default()
  },
}
</script>
